/**
 * FormationSelector Component
 * Dropdown for selecting the offensive formation.
 *
 * Keyboard behaviour lives in useListbox; the locked-row treatment matches CoverageSelector.
 */

import { useEffect, useState } from 'react';
import { FormationType, FormationSelectorProps } from '../types';
import { usePremiumStatus, useListbox } from '../hooks';
import { PREMIUM_FORMATIONS } from '../utils';
import PremiumNotice from './PremiumNotice';
import './FormationSelector.css';

const FORMATIONS: Array<{ id: FormationType; name: string }> = [
  { id: 'doubles', name: 'Doubles' },
  { id: 'trips', name: 'Trips' },
];

function FormationSelector({
  selectedFormation,
  onFormationChange,
  disabled = false,
  id = 'formation-select',
  labelledBy,
}: FormationSelectorProps) {
  const { isPremium, isLoading } = usePremiumStatus();
  const [blockedFormation, setBlockedFormation] = useState<FormationType | null>(null);

  const isLockedFormation = (formation: FormationType) =>
    PREMIUM_FORMATIONS.includes(formation) && !isPremium && !isLoading;

  const selectedName = FORMATIONS.find((f) => f.id === selectedFormation)?.name ?? selectedFormation;
  const selectedIndex = FORMATIONS.findIndex((f) => f.id === selectedFormation);

  const handleActivate = (index: number) => {
    const formation = FORMATIONS[index];
    if (!formation) return;

    // Was a silent early return. A free user clicking "Trips" got nothing back and no reason.
    if (isLockedFormation(formation.id)) {
      setBlockedFormation(formation.id);
      return;
    }

    setBlockedFormation(null);
    onFormationChange(formation.id);
    listbox.close();
  };

  const listbox = useListbox({
    itemCount: FORMATIONS.length,
    selectedIndex,
    onActivate: handleActivate,
    id,
    disabled,
  });

  useEffect(() => {
    if (!listbox.isOpen) setBlockedFormation(null);
  }, [listbox.isOpen]);

  return (
    <div className="formation-selector" ref={listbox.containerRef}>
      <div
        className={`formation-select-trigger ${disabled ? 'disabled' : ''}`}
        id={id}
        {...(labelledBy
          ? { 'aria-labelledby': labelledBy }
          : { 'aria-label': 'Select offensive formation' })}
        {...listbox.triggerProps}
      >
        <span>{selectedName}</span>
        <span className="formation-select-arrow" aria-hidden="true">▼</span>
      </div>

      {listbox.isOpen && !disabled && (
        <ul className="formation-select-dropdown" {...listbox.listProps}>
          {FORMATIONS.map((formation, index) => {
            const isLocked = isLockedFormation(formation.id);
            const isSelected = formation.id === selectedFormation;

            return (
              <li
                key={formation.id}
                className={`formation-option ${isSelected ? 'selected' : ''} ${isLocked ? 'locked' : ''}`}
                role="option"
                aria-selected={isSelected}
                {...listbox.getOptionProps(index)}
              >
                <span className="formation-option-name">{formation.name}</span>
                {isLocked && <span className="formation-premium-badge">Premium</span>}

                {blockedFormation === formation.id && (
                  <PremiumNotice
                    featureName={formation.name}
                    detail="Premium unlocks 3-receiver sets, the match coverages and saved plays."
                    onNavigate={listbox.close}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default FormationSelector;
